import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PrimaryButton, OutlineButton } from '@artprov/design-system'
import { MonthPickerDS } from '../MonthPicker/MonthPickerDS'
import { DATE_RANGE_LABELS } from '../../constants/labels'
import { createDateRangeSchema } from '../../utils/schemas/dateRangeSchema'

const getPreviousMonth = () => {
  const now = new Date()
  const month = now.getMonth()
  const year = now.getFullYear()
  if (month === 0) return `${year - 1}-12`
  return `${year}-${String(month).padStart(2, '0')}`
}

const addMonths = (yyyyMm, months) => {
  const [year, month] = yyyyMm.split('-').map(Number)
  const totalMonths = year * 12 + (month - 1) + months
  const newYear = Math.floor(totalMonths / 12)
  const newMonth = (totalMonths % 12) + 1
  return `${newYear}-${String(newMonth).padStart(2, '0')}`
}

/**
 * Selector de rango de meses reutilizable con validaciÃ³n integrada.
 * Emite fechas en formato YYYY-MM.
 *
 * @param {Function} onSubmit - Callback con { fechaDesde, fechaHasta } en formato YYYY-MM
 * @param {boolean} [isLoading=false] - Deshabilita el formulario mientras carga
 * @param {string} [submitLabel] - Texto del botÃ³n de envÃ­o
 * @param {string} [loadingLabel] - Texto del botÃ³n mientras carga
 * @param {string} [fromLabel] - Label del campo Desde
 * @param {string} [toLabel] - Label del campo Hasta
 * @param {string} [minAbsoluteDate="2017-01"] - Fecha mÃ­nima absoluta (YYYY-MM)
 * @param {number} [lookbackMonths=120] - Meses hacia atrÃ¡s desde el tope global para el mÃ­nimo de Desde
 * @param {number} [maxRangeMonths=11] - Diferencia mÃ¡xima en meses entre Desde y Hasta
 * @param {boolean} [showDownloadButton=false] - Muestra el botÃ³n de descarga
 * @param {Function} [onDownload] - Callback para el botÃ³n de descarga
 */
export const MonthRangeSelector = ({
  onSubmit,
  isLoading = false,
  submitLabel = DATE_RANGE_LABELS.GENERATE_REPORT,
  fromLabel = DATE_RANGE_LABELS.FROM,
  toLabel = DATE_RANGE_LABELS.TO,
  minAbsoluteDate = '2017-01',
  lookbackMonths = 120,
  maxRangeMonths = 11,
  showDownloadButton = false,
  onDownload,
  showPreviewButton = false,
  onPreview,
  isDownloading = false,
}) => {
  const schema = useMemo(() => createDateRangeSchema(maxRangeMonths), [maxRangeMonths])

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
  })

  const fechaDesde = watch('fechaDesde')
  // Suscripción a cambios de fechaHasta (efecto de re-render); el valor no se usa directamente
  watch('fechaHasta')

  const maxDate = useMemo(() => getPreviousMonth(), [])

  const minDateDesde = useMemo(() => {
    const computed = addMonths(maxDate, -lookbackMonths)
    return computed < minAbsoluteDate ? minAbsoluteDate : computed
  }, [maxDate, lookbackMonths, minAbsoluteDate])

  const { minDateHasta, maxDateHasta } = useMemo(() => {
    if (!fechaDesde) return { minDateHasta: undefined, maxDateHasta: maxDate }
    const computedMax = addMonths(fechaDesde, maxRangeMonths)
    return {
      minDateHasta: fechaDesde,
      maxDateHasta: computedMax < maxDate ? computedMax : maxDate,
    }
  }, [fechaDesde, maxDate, maxRangeMonths])

  useEffect(() => {
    if (!fechaDesde) return
    setValue('fechaHasta', maxDateHasta, { shouldValidate: true })
  }, [fechaDesde, maxDateHasta, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-end justify-between gap-3 sm:gap-2 w-full">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end justify-start gap-3 sm:gap-2 flex-1 w-full">
          <Controller
            name="fechaDesde"
            control={control}
            render={({ field }) => (
              <div className="w-full sm:flex-1 sm:min-w-0 sm:max-w-[296px]">
                <MonthPickerDS
                  label={fromLabel}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  minDate={minDateDesde}
                  maxDate={maxDate}
                  className="h-[36px] w-full"
                />
              </div>
            )}
          />

          <Controller
            name="fechaHasta"
            control={control}
            render={({ field }) => (
              <div className="w-full sm:flex-1 sm:min-w-0 sm:max-w-[296px]">
                <MonthPickerDS
                  label={toLabel}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                  minDate={minDateHasta}
                  maxDate={maxDateHasta}
                  className="h-[36px] w-full"
                />
              </div>
            )}
          />

          <div className="flex-none w-full sm:w-auto">
            <PrimaryButton
              size="small"
              variant="default"
              type="submit"
              disabled={!isValid || isLoading}
              className="h-[36px] w-full sm:w-auto flex items-center justify-center px-3 relative"
            >
              <span className={isLoading ? 'invisible' : undefined}>{submitLabel}</span>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                </div>
              )}
            </PrimaryButton>
          </div>
        </div>

        {(showDownloadButton || showPreviewButton) && (
          <div className="flex-none w-full sm:w-auto flex gap-2">
            {showPreviewButton && (
              <OutlineButton
                size="small"
                variant="default"
                type="button"
                onClick={onPreview}
                className="h-[36px] w-full sm:w-auto flex items-center justify-center"
              >
                Preview
              </OutlineButton>
            )}
            {showDownloadButton && (
              <OutlineButton
                size="small"
                IconLeft={isDownloading ? undefined : 'DOWNLOAD'}
                variant="default"
                type="button"
                onClick={onDownload}
                className={`h-[36px] w-full sm:w-auto flex items-center justify-center${isDownloading ? ' pointer-events-none' : ''}`}
              >
                {isDownloading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-primary border-t-transparent inline-block" />
                    Descargando
                  </span>
                ) : (
                  'Descargar'
                )}
              </OutlineButton>
            )}
          </div>
        )}
      </div>
    </form>
  )
}

MonthRangeSelector.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  fromLabel: PropTypes.string,
  toLabel: PropTypes.string,
  minAbsoluteDate: PropTypes.string,
  lookbackMonths: PropTypes.number,
  maxRangeMonths: PropTypes.number,
  showDownloadButton: PropTypes.bool,
  onDownload: PropTypes.func,
  showPreviewButton: PropTypes.bool,
  onPreview: PropTypes.func,
  isDownloading: PropTypes.bool,
}
